import React, { useState, useEffect } from 'react'
import { Button, FormControl, Alert, Spinner, Form } from 'react-bootstrap'
import { ErrorMessage, Formik } from 'formik'
import * as Yup from 'yup'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import CustomModal from '../CustomModal/CustomModal'
import {
  getEmailPricing,
  cancelEmailAutoRenewal,
  enableEmailAutoRenewal,
  fetchEmailAccounts,
  purchaseEmailAccounts,
  getUserIP
} from '../../services/email/enom'
import { DEEP_LINK } from '../../utils/deepLinks'
import useCheckScreenSize from '../../hooks/checkMobileScreen'
import './EmailSubscriptionModal.css'

const EMAIL_USERNAME_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?$|^[a-zA-Z0-9]$/

const validationSchema = Yup.object({
  username: Yup.string()
    .trim()
    .min(1, 'Please enter a username')
    .max(40, 'Username must be 40 characters or less')
    .test(
      'valid-characters',
      'Invalid email username. Only letters, numbers, dots, hyphens, and underscores are allowed.',
      (value) => {
        if (!value) return true
        return /^[a-zA-Z0-9._-]+$/.test(value)
      }
    )
    .test('valid-format', 'Invalid email username format', (value) => {
      if (!value) return true
      return EMAIL_USERNAME_REGEX.test(value)
    })
    .required('Please enter a username')
})

const EmailSubscriptionModal = ({ show, onHide, domain, onSuccess }) => {
  const [emailList, setEmailList] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [pricing, setPricing] = useState(null)
  const [step, setStep] = useState('view')
  const [existingEmails, setExistingEmails] = useState([])
  const [fetchingEmails, setFetchingEmails] = useState(false)
  const [cancellingEmails, setCancellingEmails] = useState({})

  const { userProfile } = useSelector((state) => state.manage)
  const history = useHistory()
  const { isMobile } = useCheckScreenSize()

  const domainName = domain?.includes('@') ? domain.split('@')[1] : domain

  useEffect(() => {
    if (show) {
      setPricing(getEmailPricing(userProfile?.currency))
      setEmailList([])
      setError('')
      setExistingEmails([])
      setStep('view')
      if (domainName) {
        history.push(`${DEEP_LINK.EMAILS}?domain=${encodeURIComponent(domainName)}`)
        fetchExistingEmails()
      }
    }
  }, [show, domainName, history, userProfile?.currency])

  const fetchExistingEmails = async () => {
    if (!domainName) {
      return
    }

    try {
      setFetchingEmails(true)
      const emails = await fetchEmailAccounts(domainName)
      setExistingEmails(emails || [])
    } catch (error) {
      console.error('Error fetching emails:', error)
      setError('Failed to fetch existing emails')
      setExistingEmails([])
    } finally {
      setFetchingEmails(false)
    }
  }

  const addEmail = (username) => {
    const trimmed = username.trim()
    if (!trimmed) return

    if (!/^[a-zA-Z0-9._-]+$/.test(trimmed)) {
      setError('Invalid email username. Only letters, numbers, dots, hyphens, and underscores are allowed.')
      return
    }

    if (trimmed.length > 40) {
      setError('Username must be 40 characters or less.')
      return
    }

    if (!EMAIL_USERNAME_REGEX.test(trimmed)) {
      setError('Invalid email username format.')
      return
    }

    const usernameLower = trimmed.toLowerCase()

    const existing = (existingEmails || []).map((e) => (e.serviceDetails?.username || e.email || '').toLowerCase())
    if (existing.includes(usernameLower)) {
      setError('Email already exists on this domain')
      return
    }
    setEmailList((prev) =>
      prev.some((item) => item.email === usernameLower) ? prev : [...prev, { email: usernameLower }]
    )
  }

  const removeEmail = (email) => {
    setEmailList((prev) => prev.filter((item) => item.email !== email))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (emailList.length === 0) {
      setError('Please add at least one email address')
      return
    }
    handlePayment()
  }

  const handleToggleAutoRenewal = async (email) => {
    const emailKey = `${email.domain}-${email.serviceDetails?.username || email.email}`

    try {
      setCancellingEmails((prev) => ({ ...prev, [emailKey]: true }))
      setError('')

      if (email.autoRenew !== 'disabled') {
        await cancelEmailAutoRenewal(email.domain)
      } else {
        await enableEmailAutoRenewal(email.domain)
      }

      await fetchExistingEmails()
    } catch (error) {
      console.error('Error toggling auto-renewal:', error)
      setError(error.message || 'Failed to toggle auto-renewal')
    } finally {
      setCancellingEmails((prev) => ({ ...prev, [emailKey]: false }))
    }
  }

  const handlePayment = async () => {
    if (!userProfile?.customerId) {
      setError('Customer ID not found. Please refresh the page and try again.')
      return
    }

    setLoading(true)
    setError('')

    try {
      const users = emailList.map((item) => ({
        username: item.email.split('@')[0]
      }))

      // Get user IP with fallback handling
      const userIP = await getUserIP()

      const response = await purchaseEmailAccounts({
        customerId: userProfile.customerId,
        domain: domainName,
        endUserIP: userIP,
        quantity: emailList.length,
        users,
        currency: userProfile?.currency.toUpperCase()
      })

      if (response.status === 200) {
        setStep('success')
        onSuccess?.({
          emails: emailList,
          domain: domainName,
          users: response?.data?.users || users
        })
        await fetchExistingEmails()
      } else {
        throw new Error(`Unexpected response status: ${response.status}`)
      }
    } catch (err) {
      console.error('Email purchase error:', err)
      setError(err.message || 'Failed to purchase email accounts. Please try again.')
      setStep('view')
    } finally {
      setLoading(false)
    }
  }

  const renderViewEmails = () => {
    return (
      <div>
        <h4 className="mb-2 bold color-purple-2 font-22">Email Accounts</h4>
        <div className="grey">Add & manage email addresses for your custom domain</div>

        {fetchingEmails ? (
          <div className="text-center p-4">
            <Spinner animation="border" size="sm" />
            <div className="mt-2">Loading email accounts...</div>
          </div>
        ) : (
          <>
            {existingEmails.length > 0 && (
              <div className="mt-3">
                {existingEmails.map((email, index) => (
                  <div
                    key={index}
                    className="email-item d-flex flex-column flex-md-row align-items-start align-items-md-center"
                  >
                    <div className="flex-grow-1 mb-0">
                      <div className="d-inline">
                        <a
                          href="https://mail.enom.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="email-link"
                        >
                          {(() => {
                            const fullEmail = `${email.serviceDetails?.username || email.email || 'email'}@${domainName}`
                            const truncateThreshold = isMobile ? 20 : 40
                            const shouldTruncate = fullEmail.length > truncateThreshold
                            return (
                              <span
                                className={`d-inline-block ${shouldTruncate ? 'text-truncate email-truncate' : ''}`}
                              >
                                {fullEmail}
                              </span>
                            )
                          })()}
                        </a>
                      </div>
                    </div>
                    <div className="ms-0 ms-md-2 mt-0 email-toggle-container">
                      {cancellingEmails[`${email.domain}-${email.serviceDetails?.username || email.email}`] ? (
                        <div className="d-flex align-items-center text-muted small">
                          <Spinner size="sm" animation="border" className="me-2" />
                          <span>Updating auto-renewal...</span>
                        </div>
                      ) : (
                        <Form.Check
                          type="switch"
                          id={`auto-renew-${index}`}
                          label={
                            <span className="d-block d-md-inline small">
                              {email.autoRenew === 'disabled' ? 'Auto Renewal Disabled' : 'Auto Renewal Enabled'}
                            </span>
                          }
                          checked={email.autoRenew !== 'disabled'}
                          onChange={() => handleToggleAutoRenewal(email)}
                          className={email.autoRenew === 'disabled' ? 'text-muted' : 'text-success'}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {emailList.length > 0 && (
              <div className="mt-3">
                {emailList.map((item, index) => (
                  <div key={index} className="flex mt-1 align-items-center">
                    <div className="flex-grow-1">
                      <div className="fw-bold">
                        {(() => {
                          const fullEmail = `${item.email}@${domainName}`
                          const truncateThreshold = isMobile ? 20 : 40
                          const shouldTruncate = fullEmail.length > truncateThreshold
                          return (
                            <span className={`d-inline-block ${shouldTruncate ? 'text-truncate email-truncate' : ''}`}>
                              {fullEmail}
                            </span>
                          )
                        })()}
                      </div>
                      <div className="text-muted small">Ready to add</div>
                    </div>
                    <Button variant="outline-primary" size="sm" onClick={() => removeEmail(item.email)}>
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <hr />
            <Formik
              validationSchema={validationSchema}
              onSubmit={({ username }, { resetForm }) => {
                addEmail(username)
                resetForm()
              }}
              initialValues={{ username: '' }}
              enableReinitialize
            >
              {({ handleSubmit, handleChange, values }) => (
                <form onSubmit={handleSubmit}>
                  <div className="row mb-2">
                    <div className="col-md-6">
                      <FormControl
                        name="username"
                        type="text"
                        placeholder={`Username (e.g., admin)`}
                        value={values.username || ''}
                        onChange={handleChange}
                      />
                      <ErrorMessage name="username" component="div" className="validation-error" />
                    </div>
                    <div className="col-md-6 d-flex align-items-center">
                      <span className="text-muted fs-5 fw-bold">@{domainName}</span>
                    </div>
                  </div>
                  <div className="row">
                    <div className="w-100">
                      <Button type="submit" variant="outline-primary" className="w-100">
                        Add Inbox
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
            {emailList.length > 0 && (
              <>
                <hr />
                {pricing && (
                  <div className="p-3 bg-light rounded mb-3">
                    <h6 className="bold">Pricing Summary</h6>
                    <div className="d-flex justify-content-between">
                      <span>
                        {emailList.length} Email Inbox{emailList.length !== 1 ? 's' : ''} × {pricing?.currency}
                        {pricing?.price}/year
                      </span>
                      <span className="fw-bold">
                        {pricing?.currency}
                        {(pricing?.price || 0) * emailList.length} /year
                      </span>
                    </div>
                  </div>
                )}

                <div className="mt-3">
                  <div className="d-md-none">
                    <div className="text-muted small mb-2">
                      We will automatically charge your current payment method.
                    </div>
                    <Button
                      type="submit"
                      disabled={loading || emailList.length === 0}
                      variant="primary"
                      onClick={handleSubmit}
                      className="w-100"
                    >
                      {loading && <Spinner size="sm" animation="border" />}
                      {!loading && 'Complete Purchase'}
                    </Button>
                  </div>
                  <div className="d-none d-md-block">
                    <div className="text-muted small mb-2">
                      We will automatically charge your current payment method.
                    </div>
                    <div className="text-end">
                      <Button
                        type="submit"
                        disabled={loading || emailList.length === 0}
                        variant="primary"
                        onClick={handleSubmit}
                      >
                        {loading && <Spinner size="sm" animation="border" />}
                        {!loading && 'Complete Purchase'}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    )
  }

  const handleDoneClick = () => {
    const createdEmails = [...emailList]
    setStep('view')
    setEmailList([])
    fetchExistingEmails()

    onSuccess?.({
      emails: createdEmails,
      domain: domainName,
      users: createdEmails.map((item) => ({
        username: item.email.split('@')[0]
      }))
    })
  }

  const handleModalHide = () => {
    onHide?.()

    if (history.location.pathname !== DEEP_LINK.MANAGE) {
      history.push(DEEP_LINK.MANAGE)
    }
  }

  const renderSuccess = () => (
    <div className="mt-4">
      <h4 className="mb-3">Email account created successfully</h4>
      <div>
        {emailList.map((item, index) => (
          <div key={index} className="mt-1 d-flex align-items-center">
            <div className="me-2 icon-container">
              <img src="/assets/icons/check.svg" alt="check" width="16" height="16" />
            </div>
            <div className="flex-grow-1">
              <strong>
                {(() => {
                  const fullEmail = `${item.email}@${domainName}`
                  const truncateThreshold = isMobile ? 20 : 40
                  const shouldTruncate = fullEmail.length > truncateThreshold
                  return (
                    <span
                      className={`d-inline-block ${shouldTruncate ? 'text-truncate email-truncate email-truncate-success' : ''}`}
                    >
                      {fullEmail}
                    </span>
                  )
                })()}
              </strong>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3">
        <div className="alert alert-info mb-0">
          <strong>Next Steps:</strong> You will receive an email shortly with instructions on how to access your email
          account.
        </div>
      </div>

      <Button variant="primary" onClick={handleDoneClick} className="w-100 mt-3">
        Done
      </Button>
    </div>
  )

  return (
    <CustomModal show={show} onHide={handleModalHide} size="md" centered>
      <div className="p-4">
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        {step === 'view' && renderViewEmails()}
        {step === 'success' && renderSuccess()}
      </div>
    </CustomModal>
  )
}

export default EmailSubscriptionModal
